import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Image, MapPin } from "lucide-react";
import { Editor } from "primereact/editor";
import * as Input from "../../components/Form/Input";
import { Button } from "../../components/Button";
import { EventBreadcrumb } from "../../components/EventBreadcrumb";
import { EventGalleryDisplay } from "../../components/EventGalleryDisplay";
import { EventGalleryInput } from "../../components/Form/EventGalleryInput";
import { EventInstitutionsPicker } from "../../components/Form/EventInstitutionsPicker";
import Map from "../../components/map";
import TableCredentials from "../../components/TableCredentials";
import PrintParticipants from "../../components/PrintParticipants";
import PresenceParticipants from "../../components/PresenceParticipants";
import { Loading } from "../../components/Loading";
import { IEventApcap } from "../../@types/system";
import api from "../../service/api";
import { useStore } from "../../store";
import { toastError, toastInfo, toastSuccess } from "../../utils/toast";

type EventView =
  | "detalhes"
  | "editar"
  | "credenciamento"
  | "certificados"
  | "presenca";

function viewFromPath(pathname: string): EventView {
  if (pathname.endsWith("/editar")) return "editar";
  if (pathname.endsWith("/credenciamento")) return "credenciamento";
  if (pathname.endsWith("/certificados")) return "certificados";
  if (pathname.endsWith("/presenca")) return "presenca";
  return "detalhes";
}

function viewLabel(view: EventView): string {
  switch (view) {
    case "editar":
      return "Editar";
    case "credenciamento":
      return "Credenciamento";
    case "certificados":
      return "Certificados";
    case "presenca":
      return "Lista de presença";
    default:
      return "Detalhes";
  }
}

interface IEditEvent {
  id: string;
  title: string;
  description: string;
  banner: File | null;
  banner_site: File | null;
  date_init: string;
  date_end: string;
  galleryPhotos: File[];
  latitude?: number | null;
  longitude?: number | null;
}

export function EventSubview() {
  const { eventId = "" } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get("client") || "";
  const view = viewFromPath(pathname);

  const events = useStore((store) => store.events);
  const setEvents = useStore((store) => store.setEvents);

  const [event, setEvent] = useState<IEventApcap | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mapCenter, setMapCenter] = useState<[number, number]>([
    -23.5505, -46.6333,
  ]);
  const [editEvent, setEditEvent] = useState<IEditEvent>({
    id: "",
    title: "",
    description: "",
    banner: null,
    banner_site: null,
    date_init: "",
    date_end: "",
    galleryPhotos: [],
    latitude: null,
    longitude: null,
  });
  const [allowAllInstitutions, setAllowAllInstitutions] = useState(true);
  const [institutionIds, setInstitutionIds] = useState<string[]>([]);

  const goBack = useCallback(() => navigate("/eventos"), [navigate]);

  const hydrateEdit = useCallback((data: IEventApcap) => {
    const lat = data.latitude ?? null;
    const lng = data.longitude ?? null;
    if (lat && lng) setMapCenter([lat, lng]);
    setEditEvent({
      id: data.id,
      title: data.name,
      description: data.description,
      banner: null,
      banner_site: null,
      date_init: data.initial_date,
      date_end: data.finish_date,
      galleryPhotos: [],
      latitude: lat,
      longitude: lng,
    });
  }, []);

  const loadEvent = useCallback(async () => {
    setLoading(true);
    try {
      const fromStore = events.find((item) => item.id === eventId);
      if (fromStore) {
        setEvent(fromStore);
        hydrateEdit(fromStore);
      } else {
        const { data } = await api.get<IEventApcap>(`/events/${eventId}`);
        setEvent(data);
        hydrateEdit(data);
      }

      try {
        const { data: institutions } = await api.get(
          `/events/institutions/${eventId}`
        );
        setAllowAllInstitutions(Boolean(institutions.allow_all_institutions));
        setInstitutionIds(
          institutions.allow_all_institutions
            ? []
            : (institutions.users ?? []).map(
                (user: { id: string }) => user.id
              )
        );
      } catch {
        setAllowAllInstitutions(true);
        setInstitutionIds([]);
      }
    } catch {
      toastError("Não foi possível carregar o evento");
      navigate("/eventos");
    } finally {
      setLoading(false);
    }
  }, [eventId, events, hydrateEdit, navigate]);

  useEffect(() => {
    if (eventId) loadEvent();
  }, [eventId, loadEvent]);

  useEffect(() => {
    if (editEvent.latitude && editEvent.longitude) {
      setMapCenter([editEvent.latitude, editEvent.longitude]);
    }
  }, [editEvent.latitude, editEvent.longitude]);

  const crumbs = useMemo(
    () => [
      { label: "Eventos", to: "/eventos" },
      {
        label: event?.name || "Evento",
        to: view === "detalhes" ? undefined : `/eventos/${eventId}`,
      },
      ...(view !== "detalhes" ? [{ label: viewLabel(view) }] : []),
    ],
    [event?.name, eventId, view]
  );

  const handleBannerSelected = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) return;
    setEditEvent((prev) => ({ ...prev, banner: e.target.files![0] }));
  };

  const handleBannerSiteSelected = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) return;
    setEditEvent((prev) => ({ ...prev, banner_site: e.target.files![0] }));
  };

  const handleMapClick = (position: [number, number]) => {
    setEditEvent((prev) => ({
      ...prev,
      latitude: position[0],
      longitude: position[1],
    }));
    setMapCenter(position);
  };

  async function onEdit() {
    if (
      !editEvent.title ||
      !editEvent.description ||
      !editEvent.date_init ||
      !editEvent.date_end
    ) {
      toastInfo("Preencha todos os campos");
      return;
    }

    if (!allowAllInstitutions && institutionIds.length === 0) {
      toastInfo("Selecione ao menos uma instituição ou marque “todas”.");
      return;
    }

    setSaving(true);
    try {
      await api.put("events", {
        id: eventId,
        name: editEvent.title,
        description: editEvent.description,
        initial_date: editEvent.date_init,
        finish_date: editEvent.date_end,
        latitude: editEvent.latitude ?? null,
        longitude: editEvent.longitude ?? null,
      });

      await api.put("/events/institutions", {
        event_id: eventId,
        allow_all_institutions: allowAllInstitutions,
        user_ids: allowAllInstitutions ? [] : institutionIds,
      });

      const hasBanner =
        editEvent.banner instanceof File && editEvent.banner.size > 0;
      const hasBannerSite =
        editEvent.banner_site instanceof File && editEvent.banner_site.size > 0;

      if (hasBanner || hasBannerSite) {
        const formData = new FormData();
        formData.append("id", eventId);
        if (hasBanner) formData.append("banner", editEvent.banner as File);
        if (hasBannerSite) {
          formData.append("banner_site", editEvent.banner_site as File);
        }
        await api.patch("events/update-banner", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      if (editEvent.galleryPhotos.length > 0) {
        const galleryFormData = new FormData();
        galleryFormData.append("event_id", eventId);
        editEvent.galleryPhotos.forEach((photo) => {
          galleryFormData.append("photos", photo);
        });
        await api.patch("events/add-photos", galleryFormData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      toastSuccess("Evento editado com sucesso!");
      const { data: refreshed } = await api.get("/events");
      setEvents(refreshed);
      navigate(`/eventos/${eventId}`);
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || "Erro ao editar o evento.";
      toastError(message);
    } finally {
      setSaving(false);
    }
  }

  if (loading || !event) {
    return <Loading />;
  }

  return (
    <div className="mt-2">
      <EventBreadcrumb items={crumbs} onBack={goBack} />

      {view === "detalhes" && (
        <div className="mt-4 flex w-full flex-col gap-5 divide-y divide-zinc-200 dark:divide-zinc-800">
          <div className="grid gap-3 pt-2 lg:grid-cols-form">
            <label className="text-sm font-medium text-zinc-700 dark:text-zinc-100">
              Evento
            </label>
            <Input.Root className="bg-zinc-100">
              <Input.Control value={event.name} disabled />
            </Input.Root>
          </div>

          <div className="grid gap-3 pt-5 lg:grid-cols-form">
            <label className="text-sm font-medium text-zinc-700 dark:text-zinc-100">
              Descrição
            </label>
            <Editor
              className="w-full"
              value={event.description}
              style={{ height: "320px" }}
              disabled
            />
          </div>

          <div className="grid gap-3 pt-5 lg:grid-cols-form">
            <label className="text-sm font-medium text-zinc-700 dark:text-zinc-100">
              Data inicial
            </label>
            <Input.Root className="bg-zinc-100">
              <Input.Control type="date" value={event.initial_date} disabled />
            </Input.Root>
          </div>

          <div className="grid gap-3 pt-5 lg:grid-cols-form">
            <label className="text-sm font-medium text-zinc-700 dark:text-zinc-100">
              Data final
            </label>
            <Input.Root className="bg-zinc-100">
              <Input.Control type="date" value={event.finish_date} disabled />
            </Input.Root>
          </div>

          <div className="grid gap-3 pt-5 lg:grid-cols-form">
            <label className="text-sm font-medium text-zinc-700 dark:text-zinc-100">
              Banner
            </label>
            <div className="overflow-hidden rounded border border-zinc-200 p-2 shadow-sm w-full max-w-xl">
              <img src={event.banner_url} alt={event.name} />
            </div>
          </div>

          <div className="pt-5">
            <EventGalleryDisplay data={event} />
          </div>
        </div>
      )}

      {view === "editar" && (
        <div className="mt-4 flex w-full flex-col gap-5 divide-y divide-zinc-200 dark:divide-zinc-800">
          <div className="grid gap-3 pt-2 lg:grid-cols-form">
            <label className="text-sm font-medium text-zinc-700 dark:text-zinc-100">
              Evento
            </label>
            <Input.Root>
              <Input.Control
                value={editEvent.title}
                onChange={(e) =>
                  setEditEvent({ ...editEvent, title: e.target.value })
                }
              />
            </Input.Root>
          </div>

          <div className="grid gap-3 pt-5 lg:grid-cols-form">
            <label className="text-sm font-medium text-zinc-700 dark:text-zinc-100">
              Descrição
            </label>
            <Editor
              className="w-full"
              value={editEvent.description}
              onTextChange={(e) =>
                setEditEvent({
                  ...editEvent,
                  description: e.htmlValue ?? "",
                })
              }
              style={{ height: "320px" }}
            />
          </div>

          <div className="grid gap-3 pt-5 lg:grid-cols-form">
            <label className="text-sm font-medium text-zinc-700 dark:text-zinc-100">
              Data inicial
            </label>
            <Input.Root>
              <Input.Control
                type="date"
                value={editEvent.date_init}
                onChange={(e) =>
                  setEditEvent({ ...editEvent, date_init: e.target.value })
                }
              />
            </Input.Root>
          </div>

          <div className="grid gap-3 pt-5 lg:grid-cols-form">
            <label className="text-sm font-medium text-zinc-700 dark:text-zinc-100">
              Data final
            </label>
            <Input.Root>
              <Input.Control
                type="date"
                value={editEvent.date_end}
                onChange={(e) =>
                  setEditEvent({ ...editEvent, date_end: e.target.value })
                }
              />
            </Input.Root>
          </div>

          <div className="grid gap-3 pt-5 lg:grid-cols-form">
            <label className="text-sm font-medium text-zinc-700 dark:text-zinc-100">
              Localização
            </label>
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Input.Root>
                  <Input.Prefix>
                    <MapPin className="h-5 w-5 text-zinc-500" />
                  </Input.Prefix>
                  <Input.Control
                    type="number"
                    step="any"
                    placeholder="Latitude"
                    value={editEvent.latitude ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEditEvent({
                        ...editEvent,
                        latitude: value ? parseFloat(value) : null,
                      });
                    }}
                  />
                </Input.Root>
                <Input.Root>
                  <Input.Prefix>
                    <MapPin className="h-5 w-5 text-zinc-500" />
                  </Input.Prefix>
                  <Input.Control
                    type="number"
                    step="any"
                    placeholder="Longitude"
                    value={editEvent.longitude ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEditEvent({
                        ...editEvent,
                        longitude: value ? parseFloat(value) : null,
                      });
                    }}
                  />
                </Input.Root>
              </div>
              <Map
                center={mapCenter}
                zoom={editEvent.latitude && editEvent.longitude ? 15 : 13}
                markers={
                  editEvent.latitude && editEvent.longitude
                    ? [
                        {
                          position: [
                            editEvent.latitude,
                            editEvent.longitude,
                          ],
                          title: editEvent.title || "Localização do evento",
                        },
                      ]
                    : []
                }
                onMapClick={handleMapClick}
                editable
                interactive
                height="400px"
              />
            </div>
          </div>

          <div className="grid gap-3 pt-5 lg:grid-cols-form">
            <label
              htmlFor="file_banner_site"
              className="text-sm font-medium text-zinc-700 dark:text-zinc-100"
            >
              Banner do site
            </label>
            <div className="overflow-hidden rounded border border-zinc-200 p-2 shadow-sm w-full max-w-xl">
              <img src={event.banner_site_url} alt={event.name} />
              <div className="mt-3">
                <Input.Root>
                  <Input.Prefix>
                    <Image className="h-5 w-5 text-zinc-500" />
                  </Input.Prefix>
                  <Input.Control
                    id="file_banner_site"
                    name="file_banner_site"
                    type="file"
                    accept="image/*"
                    onChange={handleBannerSiteSelected}
                  />
                </Input.Root>
              </div>
            </div>
          </div>

          <div className="grid gap-3 pt-5 lg:grid-cols-form">
            <label
              htmlFor="file_banner_app"
              className="text-sm font-medium text-zinc-700 dark:text-zinc-100"
            >
              Banner do app
            </label>
            <div className="overflow-hidden rounded border border-zinc-200 p-2 shadow-sm w-full max-w-xl">
              <img src={event.banner_url} alt={event.name} />
              <div className="mt-3">
                <Input.Root>
                  <Input.Prefix>
                    <Image className="h-5 w-5 text-zinc-500" />
                  </Input.Prefix>
                  <Input.Control
                    id="file_banner_app"
                    name="file_banner_app"
                    type="file"
                    accept="image/*"
                    onChange={handleBannerSelected}
                  />
                </Input.Root>
              </div>
            </div>
          </div>

          <div className="pt-5">
            <EventGalleryInput
              eventId={event.id}
              onPhotosChange={(photos) => {
                setEditEvent({ ...editEvent, galleryPhotos: photos });
              }}
              existingPhotos={event.gallery || []}
              disabled={saving}
              isEditing
            />
          </div>

          <div className="grid gap-3 pt-5 lg:grid-cols-form">
            <label className="text-sm font-medium text-zinc-700 dark:text-zinc-100">
              Instituições
            </label>
            <EventInstitutionsPicker
              allowAll={allowAllInstitutions}
              selectedIds={institutionIds}
              onAllowAllChange={setAllowAllInstitutions}
              onSelectedIdsChange={setInstitutionIds}
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-5">
            <Button type="button" variant="outline" onClick={goBack}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={onEdit} disabled={saving}>
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </div>
      )}

      {view === "credenciamento" && (
        <TableCredentials
          idEvent={eventId}
          data={event}
          closeCredentials={(open) => {
            if (!open) goBack();
          }}
        />
      )}

      {view === "certificados" && (
        <PrintParticipants
          idEvent={eventId}
          data={event}
          closeCredentials={(open) => {
            if (!open) goBack();
          }}
        />
      )}

      {view === "presenca" && (
        <PresenceParticipants
          idEvent={eventId}
          idClient={clientId}
          data={event}
          closeCredentials={(open) => {
            if (!open) goBack();
          }}
        />
      )}
    </div>
  );
}
